import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getLeaveRequests, deleteLeaveRequest, updateLeaveRequestStatus, clearError, clearSuccess, getEmployees, getLeaveTypes } from '../../../../redux/Slices/authSlice';
import { FaPlus, FaEdit, FaTrash, FaEye, FaCheck, FaTimes, FaPrint, FaFileExcel, FaFilter, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { chunkArray, getPdfRowStyles, toPositiveInteger } from '../../../../shared/utils.js/utils';
import SortOrderSelect from '../../../../shared/components/SortOrderSelect';
import DropdownWithSearch from '../../../../shared/components/DropdownWithSearch';

const LeaveRequests = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const { leaveRequests, leaveRequestsPagination, isLoading, error, success } = useSelector((state) => state.auth);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leaveRequestToDelete, setLeaveRequestToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10); // القيمة المطبقة فعلياً
  const [perPageInput, setPerPageInput] = useState(10); // القيمة الظاهرة في الـ UI
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortField, setSortField] = useState('id');
  const [isFilterOpen, setIsFilterOpen] = useState(true); // حالة فتح/إغلاق الفلتر
  const [allLeaveRequests, setAllLeaveRequests] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [leaveRequestToReject, setLeaveRequestToReject] = useState(null);
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [leaveRequestForPrint, setLeaveRequestForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [pdfRecords, setPdfRecords] = useState([]);
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;
  const locale = 'en-US';
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    employee_id: '',
    leave_type_id: '',
    status: '',
    start_date: '',
    end_date: '',
  });
  
  // Employee dropdown state
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);
  
  // Leave type dropdown state
  const [leaveTypeDropdownOpen, setLeaveTypeDropdownOpen] = useState(false);
  const [leaveTypeSearchTerm, setLeaveTypeSearchTerm] = useState('');
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const [leaveTypePage, setLeaveTypePage] = useState(1);
  const [leaveTypePagination, setLeaveTypePagination] = useState(null);
  const leaveTypeSearchInputRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters.employee_id, filters.leave_type_id, filters.status, filters.start_date, filters.end_date]);

  useEffect(() => {
    setTitle('sidenav.hr.leaveRequests');
  }, [setTitle]);

  const handleApplyPerPage = () => {
    // عند الضغط على تطبيق: نطبّق القيمة الجديدة ونرجع لأول صفحة
    const safeValue = toPositiveInteger(perPageInput, perPage);
    setPerPageInput(safeValue);
    setPerPage(safeValue);
    setCurrentPage(1);
  };

  const calculateDynamicChunks = (items) => {
    const tableContainer = document.getElementById('leave-requests-measurement-container');
    const thead = tableContainer ? tableContainer.querySelector('thead') : null;
    const theadHeight = thead ? thead.offsetHeight : 35;
    const rows = tableContainer ? tableContainer.querySelectorAll('tbody tr') : [];
    
    const MAX_TABLE_HEIGHT = 740;
    const chunks = [];
    let currentChunk = [];
    let currentHeight = theadHeight;

    items.forEach((item, index) => {
      const rowElement = rows[index];
      let rowHeight = 45; // Default fallback height
      
      if (rowElement && rowElement.offsetHeight > 0) {
        rowHeight = rowElement.offsetHeight;
      } else {
        const reasonLen = (item.reason || '').length;
        const nameLen = (item.employee?.name || '').length;
        const leaveTypeLen = (item.leave_type?.name || '').length;
        const maxTextLen = Math.max(reasonLen / 25, nameLen / 15, leaveTypeLen / 15);
        const lines = Math.max(1, Math.ceil(maxTextLen));
        rowHeight = 35 + (lines * 15);
      }

      if (currentHeight + rowHeight > MAX_TABLE_HEIGHT && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = theadHeight;
      }
      currentChunk.push(item);
      currentHeight += rowHeight;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    return chunks;
  };

  // Normalize list response helper
  const normalizeListResponse = (payload) => {
    if (!payload) return { items: [], pagination: null };
    
    let items = [];
    let pagination = null;
    
    if (Array.isArray(payload?.data?.data)) {
      items = payload.data.data;
      pagination = payload.data?.meta || payload.data?.pagination || null;
    } else if (Array.isArray(payload?.data)) {
      items = payload.data;
      pagination = payload.meta || payload.pagination || null;
    } else if (Array.isArray(payload?.items)) {
      items = payload.items;
      pagination = payload.pagination || payload.meta || null;
    } else if (Array.isArray(payload)) {
      items = payload;
      pagination = null;
    } else {
      items = [];
      pagination = payload.pagination || payload.meta || null;
    }
    
    if (pagination) {
      pagination = {
        current_page: pagination.current_page || pagination.page || 1,
        last_page: pagination.last_page || pagination.total_pages || 1,
        per_page: pagination.per_page || 10,
        total: pagination.total || 0,
      };
    }
    
    return { items, pagination };
  };

  // Fetch employees for dropdown
  const fetchEmployees = useCallback(
    async (page = employeePage, search = employeeSearchTerm) => {
      try {
        const payload = await dispatch(
          getEmployees({
            page,
            per_page: 10,
            search: search || undefined,
            language: i18n.language,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setEmployeeOptions(items);
        setEmployeePagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('leaveRequests.filter.fetchEmployeesError', { defaultValue: 'فشل جلب الموظفين' }), { rtl: isRTL });
      }
    },
    [dispatch, t, i18n, isRTL]
  );

  // Fetch leave types for dropdown
  const fetchLeaveTypes = useCallback(
    async (page = leaveTypePage, search = leaveTypeSearchTerm) => {
      try {
        const payload = await dispatch(
          getLeaveTypes({
            page,
            per_page: 10,
            search: search || undefined,
            language: i18n.language,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setLeaveTypeOptions(items);
        setLeaveTypePagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('leaveRequests.filter.fetchLeaveTypesError', { defaultValue: 'فشل جلب أنواع الإجازات' }), { rtl: isRTL });
      }
    },
    [dispatch, t, i18n, isRTL]
  );

  // Fetch leave requests (bulk load for client-side search/filter)
  const fetchLeaveRequests = async () => {
    try {
      const params = {
        page: 1,
        per_page: 1000,
        language: i18n.language
      };
      
      if (filters.employee_id) params.employee_id = filters.employee_id;
      if (filters.leave_type_id) params.leave_type_id = filters.leave_type_id;
      if (filters.status) params.status = filters.status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const result = await dispatch(getLeaveRequests(params));
      if (getLeaveRequests.fulfilled.match(result)) {
        const { items, pagination: paginationMeta } = normalizeListResponse(result.payload);
        setAllLeaveRequests(items);
        setPagination(paginationMeta);
      }
    } catch (err) {
      console.error('Error fetching leave requests:', err);
    }
  };

  useEffect(() => {
    fetchLeaveRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, i18n.language, location.pathname, filters]);

  // Employee dropdown effects
  useEffect(() => {
    if (!employeeDropdownOpen) return;
    const timeoutId = setTimeout(() => {
      fetchEmployees(employeePage, employeeSearchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [employeeDropdownOpen, employeePage, employeeSearchTerm, fetchEmployees]);

  // Leave type dropdown effects
  useEffect(() => {
    if (!leaveTypeDropdownOpen) return;
    const timeoutId = setTimeout(() => {
      fetchLeaveTypes(leaveTypePage, leaveTypeSearchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [leaveTypeDropdownOpen, leaveTypePage, leaveTypeSearchTerm, fetchLeaveTypes]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('[data-dropdown-container]');
      const isClickOnDropdownToggle = event.target.closest('[data-dropdown-toggle]');
      
      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setEmployeeDropdownOpen(false);
        setLeaveTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
      toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handleAdd = () => {
    navigate('/leave-requests/add');
  };

  const handleEdit = (id) => {
    navigate(`/leave-requests/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/leave-requests/details/${id}`);
  };

  const handleDelete = (leaveRequest) => {
    setLeaveRequestToDelete(leaveRequest);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (leaveRequestToDelete) {
      const idToDelete = leaveRequestToDelete.id;
      const requestToDelete = leaveRequestToDelete;
      const isPending = requestToDelete.status === 'pending';
      
      // Only optimistically remove if status is pending (can be deleted)
      if (isPending) {
        setAllLeaveRequests(prev => prev.filter(request => request.id !== idToDelete));
      }
      
      setShowDeleteModal(false);
      setLeaveRequestToDelete(null);
      
      // Then call the API
      try {
        const result = await dispatch(deleteLeaveRequest(idToDelete));
        if (deleteLeaveRequest.fulfilled.match(result)) {
          // Refresh list from server to sync pagination and elements
          fetchLeaveRequests();
        } else if (deleteLeaveRequest.rejected.match(result)) {
          // Revert on error only if we did optimistic delete
          if (isPending) {
            setAllLeaveRequests(prev => {
              const restored = [...prev, requestToDelete];
              // Sort to maintain order if needed
              return restored.sort((a, b) => {
                if (a.id && b.id) return a.id - b.id;
                return 0;
              });
            });
          }
        }
      } catch (error) {
        // Revert on error only if we did optimistic delete
        if (isPending) {
          setAllLeaveRequests(prev => {
            const restored = [...prev, requestToDelete];
            return restored.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          });
        }
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLeaveRequestToDelete(null);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };
  
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const clearFilters = () => {
    setFilters({
      employee_id: '',
      leave_type_id: '',
      status: '',
      start_date: '',
      end_date: '',
    });
    setSearchTerm('');
    setCurrentPage(1);
    // Reset dropdowns
    setEmployeeDropdownOpen(false);
    setLeaveTypeDropdownOpen(false);
    setEmployeeSearchTerm('');
    setLeaveTypeSearchTerm('');
    setEmployeePage(1);
    setLeaveTypePage(1);
  };

  const hasActiveFilters = filters.employee_id || filters.leave_type_id || filters.status || filters.start_date || filters.end_date || searchTerm.trim();

  const handleStatusChange = (id, status) => {
    if (status === 'rejected') {
      const target = allLeaveRequests.find(r => r.id === id) || { id };
      setLeaveRequestToReject(target);
      setRejectReason('');
      setShowRejectModal(true);
      return;
    }
    dispatch(updateLeaveRequestStatus({ id, status })).then(() => {
      fetchLeaveRequests();
    });
  };

  const confirmReject = () => {
    if (!leaveRequestToReject) return;
    if (!rejectReason.trim()) {
      toast.error(t('leaveRequests.rejectModal.error'), { rtl: isRTL });
      return;
    }
    dispatch(updateLeaveRequestStatus({ id: leaveRequestToReject.id, status: 'rejected', rejection_reason: rejectReason.trim() })).then(() => {
      fetchLeaveRequests();
    });
    setShowRejectModal(false);
    setLeaveRequestToReject(null);
    setRejectReason('');
  };

  const cancelReject = () => {
    setShowRejectModal(false);
    setLeaveRequestToReject(null);
    setRejectReason('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return '#28a745';
      case 'rejected':
        return '#dc3545';
      case 'pending':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return t('leaveRequests.status.approved');
      case 'rejected':
        return t('leaveRequests.status.rejected');
      case 'pending':
        return t('leaveRequests.status.pending');
      default:
        return t('leaveRequests.status.notSpecified');
    }
  };

  const filteredLeaveRequests = useMemo(() => {
    let data = [...allLeaveRequests];

    const query = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
    if (query) {
      data = data.filter((item) => {
        const empName = (item.employee?.name || '').toLowerCase();
        const empNameAr = (item.employee?.name_ar || '').toLowerCase();
        const empNameEn = (item.employee?.name_en || '').toLowerCase();
        const empCode = (item.employee?.employee_code || '').toLowerCase();
        const leaveType = (item.leave_type?.name || '').toLowerCase();
        const leaveTypeAr = (item.leave_type?.name_ar || '').toLowerCase();
        const leaveTypeEn = (item.leave_type?.name_en || '').toLowerCase();
        const reason = (item.reason || '').toLowerCase();
        const statusText = getStatusText(item.status).toLowerCase();

        return (
          empName.includes(query) ||
          empNameAr.includes(query) ||
          empNameEn.includes(query) ||
          empCode.includes(query) ||
          leaveType.includes(query) ||
          leaveTypeAr.includes(query) ||
          leaveTypeEn.includes(query) ||
          reason.includes(query) ||
          statusText.includes(query)
        );
      });
    }

    return data.sort((a, b) => {
      let aVal, bVal;

      if (sortField === 'start_date' || sortField === 'end_date') {
        aVal = new Date(a[sortField]).getTime() || 0;
        bVal = new Date(b[sortField]).getTime() || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'status') {
        aVal = getStatusText(a.status);
        bVal = getStatusText(b.status);
        if (sortOrder === 'asc') return aVal.localeCompare(bVal, isRTL ? 'ar' : 'en');
        return bVal.localeCompare(aVal, isRTL ? 'ar' : 'en');
      } else if (sortField === 'employee') {
        aVal = (a.employee?.name?.toString() || '').toLowerCase();
        bVal = (b.employee?.name?.toString() || '').toLowerCase();
        if (sortOrder === 'asc') return aVal.localeCompare(bVal, isRTL ? 'ar' : 'en');
        return bVal.localeCompare(aVal, isRTL ? 'ar' : 'en');
      } else {
        aVal = a.id || 0;
        bVal = b.id || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });
  }, [allLeaveRequests, searchTerm, sortField, sortOrder, isRTL, t]);

  const totalPages = Math.max(1, Math.ceil(filteredLeaveRequests.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);

  const paginatedLeaveRequests = useMemo(() => {
    const start = (currentPageSafe - 1) * perPage;
    return filteredLeaveRequests.slice(start, start + perPage);
  }, [filteredLeaveRequests, currentPageSafe, perPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US');
  };

  const colStyles = {
    serial: { width: '6%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px', fontSize: '10px' },
    employee: { width: '24%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px', fontSize: '10px', wordBreak: 'break-word' },
    leaveType: { width: '16%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px', fontSize: '10px' },
    startDate: { width: '13%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px', fontSize: '10px' },
    endDate: { width: '13%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px', fontSize: '10px' },
    reason: { width: '18%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px', fontSize: '10px', wordBreak: 'break-word' },
    status: { width: '10%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px', fontSize: '10px' }
  };

  const renderPdfPage = (chunk, chunkIdx, totalPages, elementId = null) => {
    const activeEmployee = filters.employee_id ? employeeOptions.find(emp => String(emp.id) === String(filters.employee_id))?.name : null;
    const activeLeaveType = filters.leave_type_id ? leaveTypeOptions.find(lt => String(lt.id) === String(filters.leave_type_id))?.name : null;
    const hasActiveFilters = filters.employee_id || filters.leave_type_id || filters.status || filters.start_date || filters.end_date || searchTerm.trim();

    return (
      <div
        id={elementId}
        style={{
          width: '794px',
          height: '1123px',
          backgroundColor: '#ffffff',
          color: '#000000',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          padding: '40px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <div style={{ 
          border: '5px solid #000',
          padding: '3px',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          height: '1043px',
          overflow: 'hidden'
        }}>
          <div style={{
            border: '2px solid #000',
            height: '1033px',
            padding: '18px 18px 10px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            direction: isRTL ? 'rtl' : 'ltr',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: '95%',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: 'none'
            }} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />

              <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>
                  {isRTL ? 'تقرير طلبات الإجازات' : 'Leave Requests Report'}
                </div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                  {isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIdx + 1} / {totalPages}
                </div>
                
                {hasActiveFilters && (
                  <div style={{ 
                    fontSize: '9px', 
                    color: '#444', 
                    marginTop: '6px', 
                    display: 'flex', 
                    gap: '12px', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap',
                    backgroundColor: 'rgba(238, 242, 247, 0.5)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}>
                    {searchTerm.trim() && <span>{t('common.search', { defaultValue: 'بحث' })}: {searchTerm.trim()}</span>}
                    {filters.employee_id && <span>{t('leaveRequests.filter.employee', { defaultValue: 'الموظف' })}: {activeEmployee || filters.employee_id}</span>}
                    {filters.leave_type_id && <span>{t('leaveRequests.filter.leaveType', { defaultValue: 'نوع الإجازة' })}: {activeLeaveType || filters.leave_type_id}</span>}
                    {filters.status && <span>{t('leaveRequests.filter.status', { defaultValue: 'الحالة' })}: {getStatusText(filters.status)}</span>}
                    {filters.start_date && <span>{t('leaveRequests.filter.startDate', { defaultValue: 'من تاريخ' })}: {formatDate(filters.start_date)}</span>}
                    {filters.end_date && <span>{t('leaveRequests.filter.endDate', { defaultValue: 'إلى تاريخ' })}: {formatDate(filters.end_date)}</span>}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: '16px', flex: 1 }}>
                <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                        <th style={colStyles.serial}>{t('common.serialNumber', { defaultValue: '#' })}</th>
                        <th style={colStyles.employee}>{t('leaveRequests.tableHeaders.employee', { defaultValue: 'الموظف' })}</th>
                        <th style={colStyles.leaveType}>{t('leaveRequests.tableHeaders.leaveType', { defaultValue: 'نوع الإجازة' })}</th>
                        <th style={colStyles.startDate}>{t('leaveRequests.tableHeaders.startDate', { defaultValue: 'تاريخ البدء' })}</th>
                        <th style={colStyles.endDate}>{t('leaveRequests.tableHeaders.endDate', { defaultValue: 'تاريخ الانتهاء' })}</th>
                        <th style={colStyles.reason}>{t('leaveRequests.tableHeaders.reason', { defaultValue: 'السبب' })}</th>
                        <th style={colStyles.status}>{t('leaveRequests.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chunk.map((request, idx) => {
                        const flatRecords = pdfChunks.flat();
                        const total = flatRecords.length;
                        const globalIdx = flatRecords.findIndex(r => String(r?.id) === String(request?.id));
                        const safeGlobalIdx = globalIdx >= 0 ? globalIdx : 0;
                        const serialNumber = sortOrder === 'desc' ? total - safeGlobalIdx : safeGlobalIdx + 1;
                        const cellBgColor = idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)';

                        return (
                          <tr key={request.id} style={{ backgroundColor: cellBgColor }}>
                            <td style={{ ...colStyles.serial, backgroundColor: cellBgColor }}>{serialNumber}</td>
                            <td style={{ ...colStyles.employee, backgroundColor: cellBgColor }}>
                              <div style={{ fontWeight: 'bold' }}>{request.employee?.name || '—'}</div>
                              {request.employee?.employee_code && (
                                <div style={{ fontSize: '8px', color: '#666' }}>{request.employee.employee_code}</div>
                              )}
                            </td>
                            <td style={{ ...colStyles.leaveType, backgroundColor: cellBgColor }}>{request.leave_type?.name || '—'}</td>
                            <td style={{ ...colStyles.startDate, backgroundColor: cellBgColor }}>{formatDate(request.start_date) || '—'}</td>
                            <td style={{ ...colStyles.endDate, backgroundColor: cellBgColor }}>{formatDate(request.end_date) || '—'}</td>
                            <td style={{ ...colStyles.reason, backgroundColor: cellBgColor }}>{request.reason || '—'}</td>
                            <td style={{ ...colStyles.status, backgroundColor: cellBgColor }}>{getStatusText(request.status)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '18px',
                right: '18px',
                flexShrink: 0
              }}>
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                      borderTop: "4px double #000",
                      paddingTop: "8px",
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Handle print single leave request
  const handlePrintLeaveRequest = async (leaveRequest) => {
    try {
      setLeaveRequestForPrint(leaveRequest);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise((resolve) => setTimeout(resolve, 400));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`leave-request-pdf-${leaveRequest.id}`);
      if (!element) {
        toast.error(t('leaveRequests.print.error', { defaultValue: 'خطأ في تحميل قالب الطباعة' }), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(`leave-request-pdf-${leaveRequest.id}`);
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = 'bold';
            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            clonedElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
            clonedElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
            clonedElement.style.textRendering = 'optimizeLegibility';
            
            const allElements = clonedElement.querySelectorAll('*');
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = 'bold';
                el.style.setProperty('-webkit-font-smoothing', 'antialiased');
                el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
                el.style.textRendering = 'optimizeLegibility';
              }
            });
          }
        }
      });
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/png', 1.0);
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Professional solution: If content slightly exceeds one page, scale it down to fit
      // This prevents extra blank pages while maintaining quality
      const tolerance = 10; // 10mm tolerance
      if (imgHeight > pdfHeight && imgHeight <= pdfHeight + tolerance) {
        // Scale down to fit exactly on one page
        const scaleFactor = pdfHeight / imgHeight;
        imgHeight = pdfHeight;
        imgWidth = pdfWidth * scaleFactor;
        
        // Center the scaled image
        const xOffset = (pdfWidth - imgWidth) / 2;
        pdf.addImage(imgData, 'PNG', xOffset, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else if (imgHeight <= pdfHeight) {
        // Fits perfectly on one page
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else {
        // Content is too large, split across multiple pages
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          const remainingHeight = imgHeight - position;
          const pageHeight = Math.min(remainingHeight, pdfHeight);
          pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight, undefined, 'FAST');
          position += pdfHeight;
        }
      }
      
      const identifier = leaveRequest.employee?.name || leaveRequest.id;
      pdf.save(`ACE_LeaveRequest_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Leave Request PDF error:', err);
      toast.error(t('leaveRequests.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setLeaveRequestForPrint(null);
      setPdfType(null);
    }
  };

  // Fetch all records (ignoring current page/perPage) for PDF export
  const fetchAllRecordsForPdf = async () => {
    const total = pagination?.total || 0;
    if (total <= allLeaveRequests.length) {
      return filteredLeaveRequests;
    }
    const params = { page: 1, per_page: total || 1000, language: i18n.language };
    if (filters.employee_id) params.employee_id = filters.employee_id;
    if (filters.leave_type_id) params.leave_type_id = filters.leave_type_id;
    if (filters.status) params.status = filters.status;
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;

    const result = await dispatch(getLeaveRequests(params));
    if (getLeaveRequests.fulfilled.match(result)) {
      const { items } = normalizeListResponse(result.payload);
      const query = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
      if (!query) return items;
      return items.filter((item) => {
        const empName = (item.employee?.name || '').toLowerCase();
        const empNameAr = (item.employee?.name_ar || '').toLowerCase();
        const empNameEn = (item.employee?.name_en || '').toLowerCase();
        const empCode = (item.employee?.employee_code || '').toLowerCase();
        const leaveType = (item.leave_type?.name || '').toLowerCase();
        const leaveTypeAr = (item.leave_type?.name_ar || '').toLowerCase();
        const leaveTypeEn = (item.leave_type?.name_en || '').toLowerCase();
        const reason = (item.reason || '').toLowerCase();
        const statusText = getStatusText(item.status).toLowerCase();
        return (
          empName.includes(query) ||
          empNameAr.includes(query) ||
          empNameEn.includes(query) ||
          empCode.includes(query) ||
          leaveType.includes(query) ||
          leaveTypeAr.includes(query) ||
          leaveTypeEn.includes(query) ||
          reason.includes(query) ||
          statusText.includes(query)
        );
      });
    }
    return filteredLeaveRequests;
  };

  // Handle preview all leave requests
  const handlePreviewAll = async () => {
    if (!filteredLeaveRequests || filteredLeaveRequests.length === 0) {
      toast.error(t('leaveRequests.preview.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const allRecords = await fetchAllRecordsForPdf();
    
    // Sort all records before chunking for PDF
    const sortedRecords = [...allRecords].sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'start_date' || sortField === 'end_date') {
        aVal = new Date(a[sortField]).getTime() || 0;
        bVal = new Date(b[sortField]).getTime() || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'status') {
        aVal = getStatusText(a.status);
        bVal = getStatusText(b.status);
        if (sortOrder === "asc") return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
        return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
      } else if (sortField === 'employee') {
        aVal = (a.employee?.name?.toString() || "").toLowerCase();
        bVal = (b.employee?.name?.toString() || "").toLowerCase();
        if (sortOrder === "asc") return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
        return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
      } else {
        aVal = a.id || 0;
        bVal = b.id || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    setPdfRecords(sortedRecords);
    await new Promise((resolve) => setTimeout(resolve, 300));

    const chunks = calculateDynamicChunks(sortedRecords);
    setPdfChunks(chunks);
    setPdfType('all');
    setIsPreviewOpen(true);
  };

  // Handle print all leave requests
  const handlePrintAll = async () => {
    if (!filteredLeaveRequests || filteredLeaveRequests.length === 0) {
      toast.error(t('leaveRequests.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setIsGeneratingPdf(true);
      const allRecords = await fetchAllRecordsForPdf();
      
      // Sort all records before chunking for PDF
      const sortedRecords = [...allRecords].sort((a, b) => {
        let aVal, bVal;
        if (sortField === 'start_date' || sortField === 'end_date') {
          aVal = new Date(a[sortField]).getTime() || 0;
          bVal = new Date(b[sortField]).getTime() || 0;
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        } else if (sortField === 'status') {
          aVal = getStatusText(a.status);
          bVal = getStatusText(b.status);
          if (sortOrder === "asc") return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
          return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
        } else if (sortField === 'employee') {
          aVal = (a.employee?.name?.toString() || "").toLowerCase();
          bVal = (b.employee?.name?.toString() || "").toLowerCase();
          if (sortOrder === "asc") return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
          return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
        } else {
          aVal = a.id || 0;
          bVal = b.id || 0;
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        }
      });

      setPdfRecords(sortedRecords);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const chunks = calculateDynamicChunks(sortedRecords);
      setPdfChunks(chunks);
      setPdfType('all');
      
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 200));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => setTimeout(resolve, 100));

        const element = document.getElementById(`leave-requests-pdf-all-${chunkIndex}`);
        if (!element) {
          if (chunkIndex === 0) {
            toast.error(t('leaveRequests.print.error', { defaultValue: 'قالب الطباعة غير متوفر' }), { rtl: isRTL });
            setIsGeneratingPdf(false);
            setPdfType(null);
            setPdfChunks([]);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById(`leave-requests-pdf-all-${chunkIndex}`);
            if (clonedElement) {
              clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = 'bold';
              clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            }
          }
        });

        const imgData = canvas.toDataURL('image/png', 1.0);

        if (chunkIndex > 0) {
          pdf.addPage('a4', 'p');
        }

        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
      }

      pdf.save(`ACE_LeaveRequests_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('leaveRequests.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Leave Requests PDF error:', error);
      toast.error(t('leaveRequests.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف طلبات الإجازات' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle export single leave request to Excel
  const handleExportLeaveRequestToExcel = (leaveRequest) => {
    const data = [{
      [t('leaveRequests.table.employee', { defaultValue: 'الموظف' })]: leaveRequest.employee?.name || '—',
      [t('leaveRequests.table.leaveType', { defaultValue: 'نوع الإجازة' })]: leaveRequest.leave_type?.name || '—',
      [t('leaveRequests.table.startDate', { defaultValue: 'تاريخ البدء' })]: formatDate(leaveRequest.start_date) || '—',
      [t('leaveRequests.table.endDate', { defaultValue: 'تاريخ الانتهاء' })]: formatDate(leaveRequest.end_date) || '—',
      [t('leaveRequests.table.reason', { defaultValue: 'السبب' })]: leaveRequest.reason || '—',
      [t('leaveRequests.table.status', { defaultValue: 'الحالة' })]: getStatusText(leaveRequest.status)
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('leaveRequests.excel.sheetName', { defaultValue: 'Leave Request' }));
    const identifier = leaveRequest.employee?.name || leaveRequest.id;
    const fileName = `${t('leaveRequests.excel.fileName', { defaultValue: 'LeaveRequest' })}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('leaveRequests.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  // Handle export all leave requests to Excel
  const handleExportAllToExcel = async () => {
    if (!filteredLeaveRequests || filteredLeaveRequests.length === 0) {
      toast.error(t('leaveRequests.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    const allRecords = await fetchAllRecordsForPdf();
    const sortedRecords = [...allRecords].sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'start_date' || sortField === 'end_date') {
        aVal = new Date(a[sortField]).getTime() || 0;
        bVal = new Date(b[sortField]).getTime() || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'status') {
        aVal = getStatusText(a.status);
        bVal = getStatusText(b.status);
        if (sortOrder === "asc") return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
        return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
      } else if (sortField === 'employee') {
        aVal = (a.employee?.name?.toString() || "").toLowerCase();
        bVal = (b.employee?.name?.toString() || "").toLowerCase();
        if (sortOrder === "asc") return aVal.localeCompare(bVal, isRTL ? "ar" : "en");
        return bVal.localeCompare(aVal, isRTL ? "ar" : "en");
      } else {
        aVal = a.id || 0;
        bVal = b.id || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    const data = sortedRecords.map((request, index) => ({
      [t('common.serialNumber', { defaultValue: '#' })]: index + 1,
      [t('leaveRequests.table.employee', { defaultValue: 'الموظف' })]: request.employee?.name || '—',
      [t('leaveRequests.table.leaveType', { defaultValue: 'نوع الإجازة' })]: request.leave_type?.name || '—',
      [t('leaveRequests.table.startDate', { defaultValue: 'تاريخ البدء' })]: formatDate(request.start_date) || '—',
      [t('leaveRequests.table.endDate', { defaultValue: 'تاريخ الانتهاء' })]: formatDate(request.end_date) || '—',
      [t('leaveRequests.table.reason', { defaultValue: 'السبب' })]: request.reason || '—',
      [t('leaveRequests.table.status', { defaultValue: 'الحالة' })]: getStatusText(request.status)
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('leaveRequests.excel.allSheetName', { defaultValue: 'Leave Requests' }));
    const fileName = `${t('leaveRequests.excel.allFileName', { defaultValue: 'LeaveRequests' })}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('leaveRequests.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };


  if (isLoading && allLeaveRequests.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        color: 'white'
      }}>
        <div>{t('common.loading', { defaultValue: 'جاري التحميل...' })}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      {/* العنوان */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold',
          color: 'white',
          margin: 0,
          textAlign
        }}>
          {t('leaveRequests.title')}
        </h1>
        
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          alignItems: 'center',
          flexDirection: isRTL ? 'row-reverse' : 'row',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, color: 'white', fontSize: 14 }}>
              <span>{t('common.sortBy', { defaultValue: 'ترتيب حسب' })}:</span>
              <select
                value={sortField}
                onChange={e => setSortField(e.target.value)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  minWidth: '120px',
                  direction: dir
                }}
              >
                <option value="id">{t('common.serialNumber', { defaultValue: '#' })}</option>
                <option value="employee">{t('leaveRequests.table.employee', { defaultValue: 'الموظف' })}</option>
                <option value="start_date">{t('leaveRequests.table.startDate', { defaultValue: 'تاريخ البدء' })}</option>
                <option value="status">{t('leaveRequests.table.status', { defaultValue: 'الحالة' })}</option>
              </select>
            </label>
            <SortOrderSelect
              value={sortOrder}
              onChange={setSortOrder}
              style={{ color: 'white', fontSize: 14 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
              {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => {
                  const newValue = Math.max(1, toPositiveInteger(perPageInput, perPage) - 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '6px 0 0 6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                step="1"
                value={perPageInput}
                onChange={(e) => {
                  setPerPageInput(toPositiveInteger(e.target.value, perPage));
                }}
                style={{
                  width: '70px',
                  padding: '6px 10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderRadius: 0,
                  color: 'white',
                  fontSize: '14px',
                  textAlign: 'center'
                }}
              />
              <button
                onClick={() => {
                  const newValue = toPositiveInteger(perPageInput, perPage) + 1;
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '0 6px 6px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: '#AC2000',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              {t('common.apply', { defaultValue: 'تطبيق' })}
            </button>
          </div>
          <button
            onClick={handleAdd}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaPlus />
            {t('leaveRequests.addButton')}
          </button>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: '#444',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaEye /> {t('leaveRequests.preview.all')}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse',
              opacity: isGeneratingPdf ? 0.6 : 1
            }}
          >
            <FaPrint />
            {isGeneratingPdf ? t('leaveRequests.print.generating') : t('leaveRequests.print.all')}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaFileExcel />
            {t('leaveRequests.export.all')}
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid #333'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: isFilterOpen ? '20px' : '0',
          flexDirection: isRTL ? 'row-reverse' : 'row',
          cursor: 'pointer'
        }}
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          {isRTL ? null : <FaFilter style={{ fontSize: '18px', color: '#AC2000' }} />}
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'white',
            margin: 0,
            textAlign: isRTL ? 'right' : 'left',
            marginLeft: isRTL ? 'auto' : 0,
            marginRight: isRTL ? 0 : 'auto'
          }}>
            {t('leaveRequests.filter.title', { defaultValue: 'فلترة' })}
          </h2>
          {isRTL ? <FaFilter style={{ fontSize: '18px', color: '#AC2000' }} /> : null}
          <span style={{ color: 'white', fontSize: '16px', marginLeft: isRTL ? '0' : '8px', marginRight: isRTL ? '8px' : '0' }}>
            {isFilterOpen ? '▼' : (isRTL ? '◀' : '▶')}
          </span>
        </div>

        {isFilterOpen && (<>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isRTL ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          {/* Search Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#ccc',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('common.search', { defaultValue: 'البحث' })}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder={t('leaveRequests.searchPlaceholder', { defaultValue: 'بحث بالموظف، الإجازة، السبب...' })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: isRTL ? '12px 35px 12px 15px' : '12px 15px 12px 35px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  minHeight: '45px',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                  boxSizing: 'border-box'
                }}
              />
              <FaSearch style={{
                position: 'absolute',
                [isRTL ? 'right' : 'left']: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888',
                fontSize: '14px'
              }} />
            </div>
          </div>

          {/* Employee Filter */}
          <div style={{ position: 'relative' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#ccc',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('leaveRequests.filter.employee', { defaultValue: 'الموظف' })}
            </label>
            <div style={{ position: 'relative' }} data-dropdown-container>
              <div
                data-dropdown-toggle
                onClick={() => {
                  const opening = !employeeDropdownOpen;
                  setEmployeeDropdownOpen(opening);
                  if (opening) {
                    setEmployeeSearchTerm('');
                    setEmployeePage(1);
                  }
                }}
                style={{
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  padding: '12px 15px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minHeight: '45px',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <span style={{ fontSize: '14px', color: filters.employee_id ? 'white' : '#888', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                  {filters.employee_id
                    ? employeeOptions.find(emp => String(emp.id) === String(filters.employee_id))?.name || t('leaveRequests.filter.selectEmployee', { defaultValue: 'اختر الموظف' })
                    : t('leaveRequests.filter.selectEmployee', { defaultValue: 'اختر الموظف' })}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              {employeeDropdownOpen && (
                <DropdownWithSearch
                  isOpen={employeeDropdownOpen}
                  searchTerm={employeeSearchTerm}
                  onSearchChange={(term) => { setEmployeeSearchTerm(term); setEmployeePage(1); }}
                  searchInputRef={employeeSearchInputRef}
                  items={employeeOptions}
                  pagination={employeePagination}
                  currentPage={employeePage}
                  onPageChange={setEmployeePage}
                  onSelect={(id) => {
                    handleFilterChange('employee_id', String(id));
                    setEmployeeDropdownOpen(false);
                    setEmployeeSearchTerm('');
                    setEmployeePage(1);
                  }}
                  label={t('leaveRequests.filter.employee', { defaultValue: 'الموظف' })}
                  isRTL={isRTL}
                />
              )}
            </div>
          </div>

          {/* Leave Type Filter */}
          <div style={{ position: 'relative' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#ccc',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('leaveRequests.filter.leaveType', { defaultValue: 'نوع الإجازة' })}
            </label>
            <div style={{ position: 'relative' }} data-dropdown-container>
              <div
                data-dropdown-toggle
                onClick={() => {
                  const opening = !leaveTypeDropdownOpen;
                  setLeaveTypeDropdownOpen(opening);
                  if (opening) {
                    setLeaveTypeSearchTerm('');
                    setLeaveTypePage(1);
                  }
                }}
                style={{
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  padding: '12px 15px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minHeight: '45px',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <span style={{ fontSize: '14px', color: filters.leave_type_id ? 'white' : '#888', textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                  {filters.leave_type_id
                    ? leaveTypeOptions.find(lt => String(lt.id) === String(filters.leave_type_id))?.name || t('leaveRequests.filter.selectLeaveType', { defaultValue: 'اختر نوع الإجازة' })
                    : t('leaveRequests.filter.selectLeaveType', { defaultValue: 'اختر نوع الإجازة' })}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              {leaveTypeDropdownOpen && (
                <DropdownWithSearch
                  isOpen={leaveTypeDropdownOpen}
                  searchTerm={leaveTypeSearchTerm}
                  onSearchChange={(term) => { setLeaveTypeSearchTerm(term); setLeaveTypePage(1); }}
                  searchInputRef={leaveTypeSearchInputRef}
                  items={leaveTypeOptions}
                  pagination={leaveTypePagination}
                  currentPage={leaveTypePage}
                  onPageChange={setLeaveTypePage}
                  onSelect={(id) => {
                    handleFilterChange('leave_type_id', String(id));
                    setLeaveTypeDropdownOpen(false);
                    setLeaveTypeSearchTerm('');
                    setLeaveTypePage(1);
                  }}
                  label={t('leaveRequests.filter.leaveType', { defaultValue: 'نوع الإجازة' })}
                  isRTL={isRTL}
                />
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#ccc',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('leaveRequests.filter.status', { defaultValue: 'الحالة' })}
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                width: '100%',
                backgroundColor: '#1a1f2e',
                border: '1px solid #444',
                borderRadius: '8px',
                padding: '12px 15px',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                minHeight: '45px',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            >
              <option value="">{t('leaveRequests.filter.allStatuses', { defaultValue: 'جميع الحالات' })}</option>
              <option value="pending">{t('leaveRequests.status.pending', { defaultValue: 'قيد الانتظار' })}</option>
              <option value="approved">{t('leaveRequests.status.approved', { defaultValue: 'موافق عليه' })}</option>
              <option value="rejected">{t('leaveRequests.status.rejected', { defaultValue: 'مرفوض' })}</option>
            </select>
          </div>

          {/* Start Date Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#ccc',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('leaveRequests.filter.startDate', { defaultValue: 'من تاريخ' })}
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              style={{
                width: '100%',
                backgroundColor: '#1a1f2e',
                border: '1px solid #444',
                borderRadius: '8px',
                padding: '12px 15px',
                color: 'white',
                fontSize: '14px',
                minHeight: '45px',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            />
          </div>

          {/* End Date Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#ccc',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('leaveRequests.filter.endDate', { defaultValue: 'إلى تاريخ' })}
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              style={{
                width: '100%',
                backgroundColor: '#1a1f2e',
                border: '1px solid #444',
                borderRadius: '8px',
                padding: '12px 15px',
                color: 'white',
                fontSize: '14px',
                minHeight: '45px',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left'
              }}
            />
          </div>
        </div>
        
        {/* Reset Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px'
        }}>
          <button
            onClick={clearFilters}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: 'pointer',
              width: 'auto',
            }}
          >
            {t('leaveRequests.filter.reset', { defaultValue: 'إعادة تعيين' })}
          </button>
        </div>
        </>
        )}
      </div>

      {/* الجدول */}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #333'
      }}>
        {paginatedLeaveRequests && paginatedLeaveRequests.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '1200px'
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #333' }}>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.number')}
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.employee')}
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.leaveType')}
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.startDate')}
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.endDate')}
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.reason')}
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.status')}
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {t('leaveRequests.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeaveRequests.map((request, index) => {
                  const recordIndex = sortOrder === 'asc' 
                    ? ((currentPageSafe - 1) * perPage) + index + 1 
                    : filteredLeaveRequests.length - (((currentPageSafe - 1) * perPage) + index);
                  return (
                  <tr key={request.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      {recordIndex}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{request.employee?.name}</div>
                        <div style={{ fontSize: '12px', color: '#ccc' }}>{request.employee?.employee_code}</div>
                      </div>
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      {request.leave_type?.name}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      {formatDate(request.start_date)}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      {formatDate(request.end_date)}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      {request.reason || '-'}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center'
                    }}>
                      <span style={{
                        backgroundColor: getStatusColor(request.status),
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        minWidth: '80px',
                        display: 'inline-block'
                      }}>
                        {getStatusText(request.status)}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        justifyContent: 'center',
                        flexWrap: 'nowrap'
                      }}>
                        <button
                          onClick={() => handleView(request.id)}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                            flexShrink: 0
                          }}
                          title={t('leaveRequests.actions.view')}
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handlePrintLeaveRequest(request)}
                          disabled={isGeneratingPdf}
                          style={{
                            backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            opacity: isGeneratingPdf ? 0.6 : 1,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={t('leaveRequests.print.single')}
                        >
                          <FaPrint />
                        </button>
                        <button
                          onClick={() => handleExportLeaveRequestToExcel(request)}
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={t('leaveRequests.export.single')}
                        >
                          <FaFileExcel />
                        </button>
                        <button
                          onClick={() => handleEdit(request.id)}
                          style={{
                            backgroundColor: '#B3B3B3',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                            flexShrink: 0
                          }}
                          title={t('leaveRequests.actions.edit')}
                        >
                          <FaEdit />
                        </button>
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(request.id, 'approved')}
                              style={{
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px',
                                flexShrink: 0
                              }}
                              title={t('leaveRequests.actions.approve')}
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={() => handleStatusChange(request.id, 'rejected')}
                              style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px',
                                flexShrink: 0
                              }}
                              title={t('leaveRequests.actions.reject')}
                            >
                              <FaTimes />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(request)}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                            flexShrink: 0
                          }}
                          title={t('leaveRequests.actions.delete')}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            {t('leaveRequests.noRecords')}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 0 && (
          <div style={{
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            borderTop: '1px solid #333',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={() => handlePageChange(Math.max(1, currentPageSafe - 1))}
              disabled={currentPageSafe === 1}
              style={{
                backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {t('common.previous', { defaultValue: 'السابق' })}
            </button>

            {(() => {
              const pages = [];
              const showPages = 5;
              let start = Math.max(1, currentPageSafe - Math.floor(showPages / 2));
              let end = Math.min(totalPages, start + showPages - 1);
              if (end - start < showPages - 1) {
                start = Math.max(1, end - showPages + 1);
              }
              for (let i = start; i <= end; i++) {
                pages.push(i);
              }
              return pages.map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  style={{
                    backgroundColor: page === currentPageSafe ? '#AC2000' : '#1a1f2e',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: page === currentPageSafe ? 'bold' : 'normal'
                  }}
                >
                  {page}
                </button>
              ));
            })()}

            <button 
              onClick={() => handlePageChange(Math.min(totalPages, currentPageSafe + 1))}
              disabled={currentPageSafe === totalPages}
              style={{
                backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {t('common.next', { defaultValue: 'التالي' })}
            </button>
          </div>
        )}
      </div>

      {/* Modal حذف */}
      {showDeleteModal && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999
        }}>
          <div style={{
            backgroundColor: '#1b3350',
            padding: '30px',
            borderRadius: '12px',
            border: '1px solid #333',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            direction: dir
          }}>
            <h3 style={{ 
              color: 'white', 
              marginBottom: '20px',
              fontSize: '18px',
              textAlign
            }}>
              {t('leaveRequests.deleteModal.title')}
            </h3>
            <p style={{ 
              color: '#ccc', 
              marginBottom: '30px',
              fontSize: '14px',
              textAlign
            }}>
              {t('leaveRequests.deleteModal.message')}
            </p>
            <p style={{ 
              color: 'white', 
              marginBottom: '30px',
              fontSize: '12px',
              textAlign
            }}>
              {t('leaveRequests.deleteModal.warning')}
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '15px', 
              justifyContent: 'center',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}>
              <button
                onClick={cancelDelete}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('leaveRequests.deleteModal.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('leaveRequests.deleteModal.confirm')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal رفض */}
      {showRejectModal && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999999
        }}>
          <div style={{
            backgroundColor: '#1b3350',
            padding: '30px',
            borderRadius: '12px',
            border: '1px solid #333',
            maxWidth: '520px',
            width: '92%',
            direction: dir
          }}>
            <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '18px', textAlign }}>{t('leaveRequests.rejectModal.title')}</h3>
            <p style={{ color: '#ccc', marginBottom: '16px', fontSize: '13px', textAlign }}>{t('leaveRequests.rejectModal.message')}</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder={t('leaveRequests.rejectModal.placeholder')}
              style={{
                width: '100%',
                padding: '12px 15px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                resize: 'vertical',
                direction: dir,
                textAlign
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '18px', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <button
                onClick={cancelReject}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('leaveRequests.rejectModal.cancel')}
              </button>
              <button
                onClick={confirmReject}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('leaveRequests.rejectModal.confirm')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Print Templates */}
      {/* Single Leave Request Print Template */}
      {leaveRequestForPrint && pdfType === 'single' && (
        <div
          id={`leave-request-pdf-${leaveRequestForPrint.id}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            minHeight: '1123px',
            height: 'auto',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}
        >
          <div style={{ 
            border: '5px solid #000',
            padding: '3px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            minHeight: '1043px',
            height: 'auto',
            overflow: 'visible'
          }}>
            <div style={{ 
              border: '2px solid #000', 
              minHeight: '1033px',
              height: '1033px',
              padding: '18px 18px 10px', 
              position: 'relative', 
              display: 'flex', 
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              direction: 'rtl',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              overflow: 'visible'
            }}>
              <div style={{ 
                position: 'absolute', 
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${headerLogo})`, 
                backgroundSize: '95%', 
                backgroundPosition: 'center center', 
                backgroundRepeat: 'no-repeat', 
                opacity: 0.08, 
                zIndex: 0, 
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                <img 
                  src={`${publicUrl}/HeaderAce.png`} 
                  alt="Header" 
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }} 
                  crossOrigin="anonymous"
                />

                <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                  <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير طلبات الإجازات' : 'Leave Requests Report'}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>ID: #{leaveRequestForPrint.id}</div>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{
                    marginTop: '12px',
                    border: '2px solid #000',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)'
                  }}>
                    {[
                      { label: 'الموظف', value: leaveRequestForPrint.employee?.name || '—' },
                      { label: 'كود الموظف', value: leaveRequestForPrint.employee?.employee_code || '—' },
                      { label: 'نوع الإجازة', value: leaveRequestForPrint.leave_type?.name || '—' },
                      { label: 'تاريخ البدء', value: formatDate(leaveRequestForPrint.start_date) || '—' },
                      { label: 'تاريخ الانتهاء', value: formatDate(leaveRequestForPrint.end_date) || '—' },
                      { label: 'السبب', value: leaveRequestForPrint.reason || '—' },
                      { label: 'الحالة', value: getStatusText(leaveRequestForPrint.status), accent: true }
                    ].filter(row => row.value && row.value !== '—').map((row) => (
                      <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                        <div style={{
                          width: '30%',
                          borderLeft: '1px solid #000',
                          backgroundColor: 'rgba(227, 242, 253, 0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          fontWeight: '700',
                          fontSize: '14px',
                          direction: 'rtl',
                          color: '#1976d2',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: 'bold',
                          padding: '0 10px'
                        }}>
                          {row.label}
                        </div>
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          fontSize: '15px',
                          fontWeight: row.accent ? '700' : '500',
                          color: row.accent ? '#0057c2' : '#000',
                          direction: 'rtl',
                          padding: '0 10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: 'bold'
                        }}>
                          {row.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ 
                  position: 'absolute',
                  bottom: '10px',
                  left: '18px',
                  right: '18px',
                  flexShrink: 0
                }}>
                  <img 
                    src={`${publicUrl}/FooterAce.png`} 
                    alt="Footer" 
                    style={{ 
                      width: '100%', 
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                      height: 'auto', 
                      objectFit: 'contain'
                    }} 
                    crossOrigin="anonymous" 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Leave Requests Print Template */}
      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && (
        <>
          {pdfChunks.map((chunk, chunkIndex) => (
            <div
              key={chunkIndex}
              style={{
                position: 'absolute',
                left: '-9999px',
                top: 0
              }}
            >
              {renderPdfPage(chunk, chunkIndex, pdfChunks.length, `leave-requests-pdf-all-${chunkIndex}`)}
            </div>
          ))}
        </>
      )}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={t('leaveRequests.title', { defaultValue: 'طلبات الإجازات' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIdx) => (
            <div key={chunkIdx} style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)' }}>
              {renderPdfPage(chunk, chunkIdx, pdfChunks.length)}
            </div>
          ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden Measurement Table */}
      <div
        id="leave-requests-measurement-container"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '714px', // 794px - 80px (padding) = 714px to match PDF page container width!
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif' }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
              <th style={colStyles.serial}>{t('common.serialNumber', { defaultValue: '#' })}</th>
              <th style={colStyles.employee}>{t('leaveRequests.tableHeaders.employee', { defaultValue: 'الموظف' })}</th>
              <th style={colStyles.leaveType}>{t('leaveRequests.tableHeaders.leaveType', { defaultValue: 'نوع الإجازة' })}</th>
              <th style={colStyles.startDate}>{t('leaveRequests.tableHeaders.startDate', { defaultValue: 'تاريخ البدء' })}</th>
              <th style={colStyles.endDate}>{t('leaveRequests.tableHeaders.endDate', { defaultValue: 'تاريخ الانتهاء' })}</th>
              <th style={colStyles.reason}>{t('leaveRequests.tableHeaders.reason', { defaultValue: 'السبب' })}</th>
              <th style={colStyles.status}>{t('leaveRequests.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
            </tr>
          </thead>
          <tbody>
            {pdfRecords.map((request, idx) => (
              <tr key={request.id}>
                <td style={colStyles.serial}>{idx + 1}</td>
                <td style={colStyles.employee}>
                  <div>{request.employee?.name || '—'}</div>
                  {request.employee?.employee_code && (
                    <div style={{ fontSize: '8px' }}>{request.employee.employee_code}</div>
                  )}
                </td>
                <td style={colStyles.leaveType}>{request.leave_type?.name || '—'}</td>
                <td style={colStyles.startDate}>{formatDate(request.start_date) || '—'}</td>
                <td style={colStyles.endDate}>{formatDate(request.end_date) || '—'}</td>
                <td style={colStyles.reason}>{request.reason || '—'}</td>
                <td style={colStyles.status}>{getStatusText(request.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};



export default LeaveRequests;
